"use client";
import { useSpring, animated, useInView, useSprings } from '@react-spring/web';
import { Target, Zap, CheckCircle, Compass, Users, Trophy, Award } from 'lucide-react';

export default function BenefitsSection() {
  const benefits = [
    {
      icon: Zap,
      title: "TRANSFORMACIÓN EN 1 SESIÓN",
      subtitle: "No más procesos eternos",
      description: "Provoca cambios profundos desde la primera sesión con la herramienta El Valor de los Valores"
    },
    {
      icon: Compass,
      title: "CLARIDAD ABSOLUTA",
      subtitle: "No más improvisación",
      description: "Sigue un proceso estructurado paso a paso que te da confianza total en cada momento"
    },
    {
      icon: Target,
      title: "MODELO TRIAXIAL",
      subtitle: "No más cambios superficiales",
      description: "Trabaja desde la raíz provocando cambios profundos y sostenibles en cualquier proceso de coaching y mentoring"
    },
    {
      icon: Users,
      title: "DINAMIZAR EQUIPOS DE TRABAJO",
      subtitle: "No más fugas de talento",
      description: "Potencia una nueva conciencia, logrando acuerdos de misión, visión y valores en sesiones ágiles y potentes"
    },
    {
      icon: Trophy,
      title: "NICHOS PREMIUM",
      subtitle: "No más competir por precio",
      description: "Experimenta la diversidad y la aplicabilidad de la herramienta"
    },
    {
      icon: Award,
      title: "HOMOLOGACIÓN ICF",
      subtitle: "No más dudas sobre tu credibilidad",
      description: "Recibe 21 CCE's de la ICF validos para renovar tu credencial como coach certificado"
    }
  ];

  // Header animation
  const [headerRef, headerInView] = useInView({ once: true, rootMargin: '-80px' });
  const headerSpring = useSpring({
    opacity: headerInView ? 1 : 0,
    config: { duration: 400 }
  });

  // Benefits cards animations
  const [benefitsRef, benefitsInView] = useInView({ once: true, rootMargin: '-80px' });
  const benefitsSprings = useSprings(
    benefits.length,
    benefits.map((_, index) => ({
      opacity: benefitsInView ? 1 : 0,
      transform: benefitsInView ? 'translateY(0px)' : 'translateY(20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Tool showcase animation
  const [toolRef, toolInView] = useInView({ once: true, rootMargin: '-80px' });
  const toolSpring = useSpring({
    opacity: toolInView ? 1 : 0,
    transform: toolInView ? 'scale(1)' : 'scale(0.95)',
    config: { duration: 400 }
  });

  return (
    <section id="benefits" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Compelling headline */}
        <animated.div
          ref={headerRef}
          className="text-center mb-16"
          style={headerSpring}
        >
          <p className="text-lg text-[#97d700] font-bold uppercase tracking-wider mb-4">
            La diferencia entre sobrevivir y brillar
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-tight text-black mb-6">
            6 cambios radicales
            <span className="block">en tu práctica profesional</span>
          </h2>
          <p className="text-xl md:text-2xl text-black/70 max-w-3xl mx-auto">
            Mientras otros coaches luchan por diferenciarse, tú tendrás la metodología que los clientes premium buscan
          </p>
        </animated.div>

        {/* Benefits grid with enhanced design */}
        <div ref={benefitsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {benefitsSprings.map((style, index) => {
            const benefit = benefits[index];
            return (
              <animated.div
                key={index}
                className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-gray-100 hover:border-[#97d700]/30"
                style={style}
              >
                <div className="w-12 h-12 bg-[#97d700]/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-[#97d700]" />
                </div>
                <h3 className="text-xl font-heading uppercase tracking-wider text-black mb-1">
                  {benefit.title}
                </h3>
                <p className="text-sm text-[#97d700] font-bold mb-4">
                  {benefit.subtitle}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {benefit.description}
                </p>
              </animated.div>
            );
          })}
        </div>

        {/* Tool showcase with enhanced positioning */}
        <animated.div
          ref={toolRef}
          className="bg-black text-white rounded-3xl p-8 md:p-12 mb-16"
          style={toolSpring}
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-[#97d700] font-bold uppercase tracking-wider mb-4">
                Tu arma secreta
              </p>
              <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-tight mb-6">
                El Valor de los Valores®
              </h3>
              <p className="text-xl mb-6 text-gray-300">
                La única herramienta en el mercado hispanohablante que trabaja con
                <span className="text-white font-bold"> 55 valores universales</span> para
                crear transformaciones medibles y duraderas
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#97d700] flex-shrink-0" />
                  <span>Proceso validado con +10.000 sesiones</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#97d700] flex-shrink-0" />
                  <span>Resultados visibles desde la primera aplicación</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#97d700] flex-shrink-0" />
                  <span>Exclusiva para coaches certificados</span>
                </li>
              </ul>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#97d700]/20 blur-3xl"></div>
                <div className="relative bg-white p-4 rounded-2xl shadow-2xl">
                  <img
                    src="/herramientaPrincipal.png"
                    alt="Herramienta El Valor de los Valores - Metodología exclusiva certificación internacional coaching con 55 valores universales"
                    width={300}
                    height={300}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </animated.div>
      </div>
    </section>
  );
}
